import { useEffect, useMemo, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
// Api
import {
  GET_NOTIFICATIONS,
  GET_READ_NOTIFICATIONS,
  GET_UNREAD_NOTIFICATIONS,
  GET_UNREAD_NOTIFICATIONS_COUNT,
} from 'api/notifications/queries';
// Helpers
import {
  enrichData,
  ExpandedNotificationData,
  filterHiddenNotifications,
} from 'helpers/notifications';
// Constants
import { FETCH_LIMIT, BUTTON_FETCH_LIMIT } from 'constants/notifications';

type UseNotificationListProps = {
  useHook: () => any;
  isButton?: boolean;
};

export const useNotificationList = ({
  useHook,
  isButton = false,
}: UseNotificationListProps) => {
  const client = useApolloClient();
  const { data, loading: rawLoading, refetch, fetchMore } = useHook();

  // data with added slugs for certain action types
  const [enrichedData, setEnrichedData] = useState<ExpandedNotificationData[]>(
    []
  );

  // flag for additional query (get store and slugs/IDs for memorabilia, merch, media feed, stream..)
  const [isEnriching, setIsEnriching] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const isFirstLoad = useRef(true);
  const nextTokenRef = useRef<string | null>(null);
  const prevNotificationsRef = useRef<Map<string, ExpandedNotificationData>>(
    new Map()
  );
  const processedIdsRef = useRef<Set<string>>(new Set());

  const notifications = useMemo(() => {
    const key = Object.keys(data || {})[0];
    const raw = data?.[key]?.data || [];
    return filterHiddenNotifications(raw);
  }, [data]);

  useEffect(() => {
    const key = Object.keys(data || {})[0];
    nextTokenRef.current = data?.[key]?.nextToken || null;
  }, [data]);

  useEffect(() => {
    const processData = async () => {
      if (!notifications.length) {
        setEnrichedData([]);
        clearRefs();
        return;
      }

      // Track which notifications we need to enrich
      const currentIds = new Set(notifications.map((n) => n.id));
      const notificationsToEnrich = notifications.filter(
        (n) => !processedIdsRef.current.has(n.id)
      );

      if (notificationsToEnrich.length === 0) {
        // We already have all notifications processed
        // Still update the order of enriched data to match notifications
        setIsLoadingMore(false);

        // Create updated list with the correct order
        const updatedData = notifications
          .map((n) => prevNotificationsRef.current.get(n.id))
          .filter(Boolean) as ExpandedNotificationData[];

        setEnrichedData(updatedData);
        return;
      }

      setIsEnriching(true);

      // Only enrich the new notifications
      const enrichedNewNotifications = await enrichData(
        notificationsToEnrich,
        client
      );

      // Store the new enriched data in our ref
      enrichedNewNotifications.forEach((notification) => {
        prevNotificationsRef.current.set(notification.id, notification);
        processedIdsRef.current.add(notification.id);
      });

      // Create final list based on original notification order
      const updatedDataList = notifications
        .map((n) => prevNotificationsRef.current.get(n.id))
        .filter(Boolean) as ExpandedNotificationData[];

      // Cleanup any notifications not in current list
      const keysToRemove: string[] = [];
      prevNotificationsRef.current.forEach((_, key) => {
        if (!currentIds.has(key)) {
          keysToRemove.push(key);
        }
      });

      keysToRemove.forEach((key) => {
        prevNotificationsRef.current.delete(key);
        processedIdsRef.current.delete(key);
      });

      setEnrichedData(updatedDataList);
      setIsEnriching(false);
      setIsLoadingMore(false);
      isFirstLoad.current = false;
    };

    processData();
  }, [notifications, client]);

  const clearRefs = () => {
    // Clear enrichment tracking
    prevNotificationsRef.current.clear();
    processedIdsRef.current.clear();
  };

  const handleLoadMore = () => {
    if (isLoadingMore || !nextTokenRef.current) {
      return;
    }

    setIsLoadingMore(true);
    const key = Object.keys(data || {})[0];

    fetchMore({
      variables: {
        input: {
          limit: isButton ? BUTTON_FETCH_LIMIT : FETCH_LIMIT,
          nextToken: nextTokenRef.current,
        },
      },
      updateQuery: (prev: any, { fetchMoreResult }: any) => {
        if (!fetchMoreResult) {
          return prev;
        }

        return {
          [key]: {
            ...fetchMoreResult[key],
            data: [...prev[key].data, ...fetchMoreResult[key].data],
          },
        };
      },
    });
  };

  const handleUpdate = async () => {
    clearRefs();
    await client.refetchQueries({
      include: [
        GET_NOTIFICATIONS,
        GET_READ_NOTIFICATIONS,
        GET_UNREAD_NOTIFICATIONS,
        GET_UNREAD_NOTIFICATIONS_COUNT,
      ],
    });

    await refetch();
  };

  const showFullLoader = rawLoading || (isFirstLoad.current && isEnriching);

  return {
    notifications: enrichedData,
    loading: showFullLoader,
    onUpdate: handleUpdate,
    onLoadMore: handleLoadMore,
  };
};
